/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Observable } from 'rxjs';
import { DevLoggerService } from 'dev-logger-service';
import { L10nService } from '@vmw/ngx-vip';
import { isDistinct } from 'ng/shared/utils';

import {
    IAviBarGraphColors,
    IAviBarGraphDataPoint,
    IAviBarGraphDataPointValueInput,
} from 'ng/modules/diagram/components/avi-bar-graph/avi-bar-graph.types';

import { ILogsGraphStates, LogsGraphStore } from 'ng/modules/logs/services/logs-graph.store';
import { ILogsGraphResponseResultData, TLogsGraphStateParams } from '../../../logs.types';

import { ILogsStates, LogsStore } from '../../../services/logs.store';
import { LogsEffectsService } from '../../../services/logs-effects.service';
import { LogsApiRequestService } from '../../../services/logs-api-request.service';
import * as l10n from './traffic-logs-graph.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

export interface ITrafficLogsGraphStates extends ILogsGraphStates {
    logsGraphResults: ILogsGraphResponseResultData[];
}

const initialState: ITrafficLogsGraphStates = {
    end: String(),
    start: String(),
    hasError: false,
    isLoading: true,
    legendList: {},
    logsGraphResults: [],
};

const logsGraphColorLegendMap: IAviBarGraphColors = {
    nonSignificant: {
        color: 'var(--avi-clr-prussian-blue)',
        label: '',
    },
    significant: {
        color: 'var(--avi-clr-viking-blue)',
        label: '',
    },
    userDefined: {
        color: 'var(--avi-purple-heart-purple)',
        label: '',
    },
    missing: {
        label: '',
        description: '',
    },
};

/**
 * @abstract
 * @description
 *     An abstract/template component store for traffic log graph.
 *     Should be inherited by more concrete traffic logs graph component stores (eg. PoolLogsGraphStore) to serve a
 *     federated purpose.
 *     The concrete store should override the logGraphRequestId property to the end component graph request ID.
 * @author Yogesh Patil
 */
export abstract class TrafficLogsGraphStore extends LogsGraphStore<ITrafficLogsGraphStates> {
    // ********************************** Selectors **********************************

    public readonly graphBarData$: Observable<IAviBarGraphDataPoint[]> =
        this.select(({ logsGraphResults }) => {
            return logsGraphResults.map(
                (result: ILogsGraphResponseResultData): IAviBarGraphDataPoint => {
                    const values: IAviBarGraphDataPointValueInput[] = [];
                    const tooltipValues: IAviBarGraphDataPointValueInput[] = [];

                    const {
                        adf,
                        end,
                        start,
                        missing_count: missingCount,
                        nf,
                        udf,
                        value: totalValueSummation,
                    } = result;

                    if (adf) {
                        const dataPointValue = {
                            color: logsGraphColorLegendMap.significant.color,
                            count: adf,
                            type: logsGraphColorLegendMap.significant.label,
                        };

                        values.push(dataPointValue);
                        tooltipValues.push(dataPointValue);
                    }

                    if (nf) {
                        const dataPointValue = {
                            color: logsGraphColorLegendMap.nonSignificant.color,
                            count: nf || 0,
                            type: logsGraphColorLegendMap.nonSignificant.label,
                        };

                        values.push(dataPointValue);
                        tooltipValues.push(dataPointValue);
                    }

                    if (udf) {
                        const dataPointValue = {
                            color: logsGraphColorLegendMap.userDefined.color,
                            count: udf || 0,
                            type: logsGraphColorLegendMap.userDefined.label,
                        };

                        values.push(dataPointValue);
                        tooltipValues.push(dataPointValue);
                    }

                    if (missingCount) {
                        const dataPointValue = {
                            count: missingCount,
                            type: logsGraphColorLegendMap.missing.label,
                            alert: true,
                            description: logsGraphColorLegendMap.missing.description,
                        };

                        tooltipValues.push(dataPointValue);
                    }

                    return {
                        end,
                        start,
                        totalValueSummation,
                        values,
                        tooltipValues,
                    };
                },
            );
        }).pipe(
            isDistinct(),
        );

    public readonly currentGraphColorsLegend$ =
        this.select(({ legendList }) => legendList);

    // ********************************** Effects **********************************

    /**
     * Call logs API for logs graph data until all data is returned or timeout.
     * Upon each API return, it updates the state and makes a new request if needed.
     */
    public override readonly getLogsGraphData: (apiParams: TLogsGraphStateParams) => void;

    /**
     * Fetch updated logs graph data.
     */
    public override readonly refreshGraph: () => void;

    // ********************************** Constructor **********************************

    constructor(
        protected readonly logsStore: LogsStore<ILogsStates>,
        logsEffectsService: LogsEffectsService,
        protected readonly logsApiRequestService: LogsApiRequestService,
        protected readonly devLoggerService: DevLoggerService,
        l10nService: L10nService,
    ) {
        super(initialState, logsStore, logsApiRequestService, devLoggerService);

        l10nService.registerSourceBundles(dictionary);

        // Initialize request and refresh effects
        const requestLogsGraphDataEffect = logsEffectsService.createLogsRecursiveRequestEffect(
            this,
            this.startLoading,
            this.stopLoading,
            this.handleApiData,
            this.handleApiError,
            this.handleApiLoopComplete,
        );

        this.getLogsGraphData = (apiParams: TLogsGraphStateParams): void => {
            requestLogsGraphDataEffect({
                apiParams,
                config: {
                    requestId: this.logGraphRequestId,
                },
            });
        };

        this.refreshGraph = this.effect(
            LogsEffectsService.createRefreshEffect<TLogsGraphStateParams>(
                logsStore.logsGraphApiParams$,
                this.getLogsGraphData,
            ),
        );

        const {
            nonSignificantLabel,
            significantLabel,
            userDefinedLabel,
            missingLabel,
            missingDescription,
        } = l10nKeys;

        logsGraphColorLegendMap.significant.label = l10nService.getMessage(significantLabel);
        logsGraphColorLegendMap.nonSignificant.label =
            l10nService.getMessage(nonSignificantLabel);
        logsGraphColorLegendMap.userDefined.label =
            l10nService.getMessage(userDefinedLabel);
        logsGraphColorLegendMap.missing.label = l10nService.getMessage(missingLabel);
        logsGraphColorLegendMap.missing.description = l10nService.getMessage(missingDescription);

        this.subscribeToBooleanRequestParamsChange();
    }

    // ********************************** Methods **********************************

    /**
     * Subscribe to significant/non-significant boolean changes from logs store.
     */
    private subscribeToBooleanRequestParamsChange = (): void => {
        this.logsStore.logParamRequestTypesSelected$.subscribe(types => {
            this.patchState(state => {
                const legendList = types.reduce((result, type) => {
                    return {
                        ...result,
                        [type]: logsGraphColorLegendMap[type],
                    };
                }, {});

                return {
                    ...state,
                    legendList,
                };
            });
        });
    };

    public override cancelLogsGraphRequest = (): void => {
        this.logsApiRequestService.cancelRequest(this.logGraphRequestId);
    };
}
